'use client'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCalculateSizeMutation } from '@/hooks/react-query/size/use-size'
import { logger } from '@/lib/logger'
import { LocalStorage } from '@/lib/utils/local-storage'
import { EProductActiveWomenType, TCalculateSizeParams } from '@/types/size'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { FieldName, useForm } from 'react-hook-form'
import { z } from 'zod'
import { SIZE_CONFIG } from '../../../../../../lib/constants/size'
import { useProductDetailSafe } from '../../context/product-detail-context'

type Props = {
  sizeChart: string
}

interface StoredSizeData {
  [key: string]: number | string
  type: string
}

export const CalculateSize = ({ sizeChart }: Props) => {
  const sizeConfig = SIZE_CONFIG[sizeChart]
  const t = useTranslations('product_detail.size_chart.calculate_size')
  const tSizeChart = useTranslations()

  const [calculatedSize, setCalculatedSize] = useState<string | null>(null)
  const { setSizeSelected } = useProductDetailSafe()
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const { mutateAsync: calculateSize, isPending } = useCalculateSizeMutation()

  // Tạo schema động dựa trên cấu hình size
  const FormSchema = z.object({
    [sizeConfig.xAxis]: z
      .number({
        required_error: t('validation.required_error'),
        invalid_type_error: t('validation.invalid_type_error'),
      })
      .min(
        sizeConfig.minXAxis,
        t('validation.range_error', {
          min: sizeConfig.minXAxis,
          max: sizeConfig.maxXAxis,
          unit: sizeConfig.unitX,
        })
      )
      .max(
        sizeConfig.maxXAxis,
        t('validation.range_error', {
          min: sizeConfig.minXAxis,
          max: sizeConfig.maxXAxis,
          unit: sizeConfig.unitX,
        })
      ),
    [sizeConfig.yAxis]: z
      .number({
        required_error: t('validation.required_error'),
        invalid_type_error: t('validation.invalid_type_error'),
      })
      .min(
        sizeConfig.minYAxis,
        t('validation.range_error', {
          min: sizeConfig.minYAxis,
          max: sizeConfig.maxYAxis,
          unit: sizeConfig.unitY,
        })
      )
      .max(
        sizeConfig.maxYAxis,
        t('validation.range_error', {
          min: sizeConfig.minYAxis,
          max: sizeConfig.maxYAxis,
          unit: sizeConfig.unitY,
        })
      ),
  })

  type FormValues = z.infer<typeof FormSchema>

  // Get saved values from localStorage
  const savedData = LocalStorage.get<StoredSizeData>('FEMALE_SIZE_DATA')

  // Extract only the numeric values for form initialization
  // Chỉ check type với BRA hoặc LEGGING, còn lại dùng chung height, weight
  // Nhưng nếu savedData.type là BRA hoặc LEGGING thì các type khác phải dùng default
  const shouldUseSavedData =
    savedData &&
    (sizeChart === 'BRA' || sizeChart === 'LEGGING'
      ? savedData.type === sizeChart
      : savedData.type !== 'BRA' && savedData.type !== 'LEGGING') // Các type khác chỉ dùng savedData nếu savedData.type không phải BRA hoặc LEGGING

  const initialData = shouldUseSavedData
    ? {
        [sizeConfig.xAxis]: Number(savedData[sizeConfig.xAxis]),
        [sizeConfig.yAxis]: Number(savedData[sizeConfig.yAxis]),
      }
    : {
        [sizeConfig.xAxis]: sizeConfig.minXAxis,
        [sizeConfig.yAxis]: sizeConfig.minYAxis,
      }

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: {
      [sizeConfig.xAxis]:
        initialData?.[sizeConfig.xAxis] ?? sizeConfig.minXAxis,
      [sizeConfig.yAxis]:
        initialData?.[sizeConfig.yAxis] ?? sizeConfig.minYAxis,
    },
  })

  const onSubmit = async (values: FormValues) => {
    const xKey = sizeConfig.xAxis as keyof FormValues
    const yKey = sizeConfig.yAxis as keyof FormValues

    const requestData: TCalculateSizeParams = {
      type: sizeChart as EProductActiveWomenType,
      [xKey]: values[xKey],
      [yKey]: values[yKey],
    }
    try {
      const res = await calculateSize(requestData)
      setCalculatedSize(res?.data?.size || '')
      setHasSubmitted(true)
      if (setSizeSelected) {
        setSizeSelected(res?.data?.size || '')
      }
    } catch (error) {
      logger.error('CalculateSize', error)
      setCalculatedSize('')
      setHasSubmitted(true)
    }
  }

  const xAxisValue = form.watch(sizeConfig.xAxis as FieldName<FormValues>)
  const yAxisValue = form.watch(sizeConfig.yAxis as FieldName<FormValues>)

  // Save to localStorage whenever values change
  useEffect(() => {
    if (xAxisValue && yAxisValue) {
      const dataToStore: StoredSizeData = {
        [sizeConfig.xAxis]: Number(xAxisValue),
        [sizeConfig.yAxis]: Number(yAxisValue),
        type: sizeChart,
      }
      LocalStorage.set('FEMALE_SIZE_DATA', dataToStore)
    }
  }, [xAxisValue, yAxisValue, sizeConfig.xAxis, sizeConfig.yAxis, sizeChart])

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-3'>
          <div className='flex items-start gap-6'>
            <FormField
              control={form.control}
              name={sizeConfig.xAxis as FieldName<FormValues>}
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{tSizeChart(sizeConfig.xAxisLabel)}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        type='number'
                        inputMode='numeric'
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder={t('placeholder')}
                        min={sizeConfig.minXAxis}
                        max={sizeConfig.maxXAxis}
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            {sizeConfig.unitX}
                          </p>
                        }
                        onChange={(e) => {
                          const rawValue = e.target.value
                          if (rawValue === '') {
                            field.onChange('')
                            return
                          }
                          let value = parseInt(rawValue, 10)
                          if (!isNaN(value)) {
                            if (value > sizeConfig.maxXAxis) {
                              value = sizeConfig.maxXAxis
                            }
                            field.onChange(value)
                          }
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value
                          let value = parseInt(rawValue, 10)
                          if (isNaN(value) || value < sizeConfig.minXAxis) {
                            value = sizeConfig.minXAxis
                          }
                          field.onChange(value)
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={sizeConfig.yAxis as FieldName<FormValues>}
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{tSizeChart(sizeConfig.yAxisLabel)}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        type='number'
                        inputMode='numeric'
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder={t('placeholder')}
                        min={sizeConfig.minYAxis}
                        max={sizeConfig.maxYAxis}
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            {sizeConfig.unitY}
                          </p>
                        }
                        onChange={(e) => {
                          const rawValue = e.target.value
                          if (rawValue === '') {
                            field.onChange('')
                            return
                          }
                          let value = parseInt(rawValue, 10)
                          if (!isNaN(value)) {
                            if (value > sizeConfig.maxYAxis) {
                              value = sizeConfig.maxYAxis
                            }
                            field.onChange(value)
                          }
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value
                          let value = parseInt(rawValue, 10)
                          if (isNaN(value) || value < sizeConfig.minYAxis) {
                            value = sizeConfig.minYAxis
                          }
                          field.onChange(value)
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button
            variant='black'
            type='submit'
            className='mt-3'
            disabled={isPending}
          >
            {isPending ? t('button.calculating') : t('button.calculate')}
          </Button>
        </form>
      </Form>

      {hasSubmitted &&
        (calculatedSize && calculatedSize !== 'Không tìm thấy' ? (
          <p className='mt-3 rounded bg-secondary-50 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'>
            {t('result.success', { size: calculatedSize })}
          </p>
        ) : (
          <p className='mt-3 rounded bg-secondary-50 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'>
            {t('result.not_found')}
          </p>
        ))}
    </div>
  )
}
